import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useAccountUsersFieldConfig,
  useLocationFieldConfig,
  useNoteTakerConfig,
  useOfficeFieldConfig,
  useInterviewerPoolConfig,
} from '@/formConfigs';
import { INTERVIEW_ACTIVITY_COPY } from '../copy';
import { useIntegrations } from '@/hooks/use-integrations';
import { useHackerRankTemplates } from './useHackerRankTemplates';
import { useAllSharedCalendarsQuery } from '@/api/sharedCalendars';
import { useSharedCalendarSetting } from '@/api/useSharedCalendar';
import { useMailBox } from '@/hooks/use-mailbox';
import { useKFlags } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';

const COPY = INTERVIEW_ACTIVITY_COPY.details;

// Sentinel picker value meaning "no plan-set calendar — defer to whoever
// schedules". Matches `hydrateFromApi`'s `calendar_info_picker: ''`
// fallback when the saved plan has no `calendar_info`, and is what the
// schedule wizard's `useScheduleCalendarData` treats as "use the
// recruiter's primary calendar at schedule time".
const ORGANIZER_CALENDAR_VALUE = '';

const INTERVIEW_NAME_VARIABLES = {
  org: ['org.name'],
  job: ['job.title', 'job.current_stage'],
  candidate: [
    'candidate.first_name',
    'candidate.last_name',
    'candidate.full_name',
  ],
};

export const useInterviewActivityDetailsSchema = (options?: {
  stageName?: string;
  selectedActivity?: string;
  participantDefaultOptions?: Array<{
    value: string;
    label: string;
    description?: string;
    image?: string;
  }>;
  /**
   * Org "AI notetaker mandatory" sourced from the plan template
   * (`show_template`). Needed because the settings interview-plan flow
   * never populates `atsJobCreate.currentJob`, so `useNoteTakerConfig`
   * can't read mandatory from Redux there.
   */
  noteTakerMandatory?: boolean;
  /**
   * True when editing an existing activity. The admin's default-calendar
   * prefill is a create-only default — on edit we keep the Organizer
   * sentinel so a plan saved as "Organizer's calendar" (which hydrates
   * with no calendar field) isn't silently switched to the default.
   */
  isEdit?: boolean;
}) => {
  const {
    stageName,
    selectedActivity,
    participantDefaultOptions,
    noteTakerMandatory: noteTakerMandatoryOverride,
    isEdit,
  } = options ?? {};
  const { isHackerRankEnabled } = useIntegrations();
  const isHackerRankActivity = selectedActivity === 'hackerrank_interview';

  // Single source of truth for the AI notetaker tile — reused by the
  // Schedule wizard and this Interview Activity schema so copy + Figma
  // styling stay in sync. Tweaks should land in `useNoteTakerConfig`.
  const { field: noteTakerField, isMandatory: noteTakerMandatory } =
    useNoteTakerConfig({ mandatoryOverride: noteTakerMandatoryOverride });

  const { fieldConfig: interviewerField } = useAccountUsersFieldConfig({
    name: 'participant_ids',
    label: 'Add interviewers',
    placeholder: 'Search interviewers',
    condition: {
      operator: 'or',
      conditions: [
        {
          operator: 'and',
          conditions: [
            { operator: 'equal', field: 'interview_type', value: 'one_on_one' },
            {
              operator: 'equal',
              field: 'interviewTypeRadio',
              value: 'manual_selection',
            },
          ],
        },
        { operator: 'equal', field: 'interview_type', value: 'panel' },
      ],
    },
    fieldProps: participantDefaultOptions?.length
      ? { defaultOptions: participantDefaultOptions }
      : undefined,
  });

  // HackerRank template options — fetched first so the location field
  // can read the `userHasNoAccount` signal and disable the HR option
  // when the current user lacks a personal HR for Work account.
  const {
    templateOptions: hackerRankTemplateOptions,
    isLoading: hrLoading,
    userHasNoAccount: hrUserHasNoAccount,
  } = useHackerRankTemplates({ enabled: Boolean(isHackerRankEnabled) });

  const { fieldConfig: locationField, defaultLocation } =
    useLocationFieldConfig({
      isHackerRank: isHackerRankActivity,
      hackerRankUserHasNoAccount: hrUserHasNoAccount,
    });

  const { fieldConfig: officeField } = useOfficeFieldConfig({});

  const interviewerPoolFields = useInterviewerPoolConfig();

  // ── Shared-calendar selection ──────────────────────────────────────────
  // Gated on the scheduling-preference flag + a connected calendar AND the
  // account-level shared-calendar switch (`shared_calendar_enabled` via
  // `GET /shared_calendars/show_setting`) — the SAME authoritative gate the
  // Schedule wizard's `useScheduleCalendarData` and `useSharedCalendarConfig`
  // use. The switch is owned by Settings → Scheduling preferences, NOT
  // authored here, so when it's OFF there's no shared calendar to pick: hide
  // the field entirely and skip the (otherwise 422-ing `err_feature_not_enabled`)
  // list fetch. The plan still authors WHICH calendar via `calendar_info`
  // when the feature is on. BE permits `calendar_info: { provider_calendar_id,
  // is_primary }` on the Job Stage Action endpoint (CORE-4039 →
  // ats_job_stage_actions_controller#ats_job_stage_action_params).
  const { feSchedulingPreference } = useKFlags() as {
    feSchedulingPreference?: boolean;
  };
  const currUser = useSelector(selectCurrUser) as
    | { connected_calendar?: { gcal?: boolean; mscal?: boolean } }
    | null
    | undefined;
  const hasConnectedCalendar = Boolean(
    currUser?.connected_calendar?.gcal || currUser?.connected_calendar?.mscal
  );
  const { isCalConnected } = useMailBox();
  const {
    data: sharedCalendarEnabled,
    isPending: isSharedSettingLoading,
    isError: isSharedSettingError,
  } = useSharedCalendarSetting();
  const shouldShowCalendarRow = Boolean(
    feSchedulingPreference &&
    hasConnectedCalendar &&
    !isSharedSettingLoading &&
    !isSharedSettingError &&
    sharedCalendarEnabled === true
  );

  // Plan editor uses the unfiltered index endpoint so a coordinator
  // can preselect a teammate's calendar even though they themselves
  // don't have edit access. The runtime scheduling surface
  // (`useScheduleCalendarData`) keeps the access-filtered list — that
  // gate is the right one at schedule-time.
  const { data: sharedCalendars, isLoading: isCalendarsLoading } =
    useAllSharedCalendarsQuery({
      enabled: shouldShowCalendarRow && isCalConnected,
    });

  const calendars = useMemo(
    () =>
      (sharedCalendars ?? []).filter(
        (c) => c?.status !== 'deleted_from_provider'
      ),
    [sharedCalendars]
  );

  // Admin's default calendar from Scheduling preferences (`is_default`).
  // When set, a new plan prefills it; otherwise we fall back to the
  // "Organizer's calendar" sentinel. Mirrors the schedule wizard's
  // `useScheduleCalendarData` default-calendar resolution.
  const defaultCalendar = useMemo(
    () =>
      isEdit
        ? undefined
        : calendars.find(
            (c) => c.is_default && typeof c.provider_calendar_id === 'string'
          ),
    [calendars, isEdit]
  );

  // Sentinel option representing "leave unbound — at schedule time, use
  // whoever ends up organizing the interview". Persisted as the absence
  // of `calendar_info` on the wire; the schedule wizard's
  // `useScheduleCalendarData` falls back to the recruiter's primary
  // calendar when nothing is set on the plan.
  const calendarOptions = useMemo(
    () => [
      { value: ORGANIZER_CALENDAR_VALUE, label: "Organizer's calendar" },
      ...calendars
        .filter(
          (c): c is typeof c & { provider_calendar_id: string } =>
            typeof c.provider_calendar_id === 'string'
        )
        .map((c) => ({
          value: c.provider_calendar_id,
          label: `${c.name ?? c.provider_calendar_id}${c.is_primary ? ' (You)' : ''}`,
        })),
    ],
    [calendars]
  );

  const schema = useMemo(
    () => [
      {
        type: 'sectionLabel',
        label: COPY.sectionLabel,
      },
      {
        type: 'variableInput' as const,
        name: 'interviewName',
        label: 'Interview name',
        required: true,
        placeholder: 'Add interview name here',
        maxLength: 250,
        fieldProps: {
          variables: INTERVIEW_NAME_VARIABLES,
        },
      },
      {
        type: 'select' as const,
        name: 'interview_type',
        label: 'Interview type',
        options: [
          {
            value: 'one_on_one',
            label: 'One-on-one interview',
            description:
              'Add the interviewer you want or pick an interviewer pool',
          },
          {
            value: 'panel',
            label: 'Panel interview',
            description:
              'Add all the interviewers you want to attend this interview',
          },
        ],
      },
      {
        type: 'radio' as const,
        name: 'interviewTypeRadio',
        condition: {
          operator: 'equal',
          field: 'interview_type',
          value: 'one_on_one',
        },
        options: [
          {
            value: 'manual_selection',
            label: 'Interviewers',
            description: 'Manually select interviewers for this interview',
          },
          {
            value: 'interview_pool',
            label: 'Interviewer pool',
            description: 'Select interviewers from an interviewer pool',
          },
        ],
      },
      interviewerField,
      ...interviewerPoolFields,
      {
        type: 'sectionLabel',
        label: COPY.scheduleSectionLabel,
      },
      // Shared-calendar picker (top of "Schedule details", before duration).
      // Persisted as `calendar_info: { provider_calendar_id, is_primary }`
      // — same shape the Schedule wizard already prefills from on
      // `fromPlan.ts`, so picking a calendar here propagates to every
      // interview created from this plan with no other wiring.
      ...(shouldShowCalendarRow
        ? [
            {
              type: 'select' as const,
              name: 'calendar_info_picker',
              label: 'Calendar',
              // Defensive placeholder only — the picker always has a
              // value selected (organizer by default, a real calendar
              // after picking) and offers no clear control, so this
              // copy is unreachable in practice. Kept so the field
              // never falls back to FormBuilder's generic placeholder
              // if the picker ever ends up unset.
              placeholder: 'Search calendars',
              options: calendarOptions,
              fieldProps: {
                loading: isCalendarsLoading,
                searchable: false,
              },
              // The Select's value is the provider id (or '' for the
              // organizer sentinel). We additionally write the
              // structured `calendar_info` object — `undefined` when
              // organizer is picked so BE stores no binding and the
              // schedule wizard falls back at schedule time.
              onChange: (
                value: unknown,
                formMethods: {
                  setValue: (_n: string, _v: unknown, _o?: unknown) => void;
                }
              ) => {
                const id = Array.isArray(value)
                  ? value[0]
                  : (value as string | undefined);
                if (typeof id !== 'string') return;
                if (id === ORGANIZER_CALENDAR_VALUE) {
                  formMethods.setValue('calendar_info', undefined);
                  return;
                }
                const cal = calendars.find(
                  (c) => c.provider_calendar_id === id
                );
                formMethods.setValue('calendar_info', {
                  provider_calendar_id: id,
                  is_primary: Boolean(cal?.is_primary),
                });
              },
            },
          ]
        : []),
      {
        type: 'numberStepper' as const,
        name: 'duration',
        label: COPY.duration.label,
        min: 5,
        max: 1440,
        fieldProps: {
          step: 15,
          min: 5,
          max: 1440,
          unitLabel: COPY.duration.unit,
        },
      },
      {
        type: 'switch' as const,
        name: 'calendar_visibility',
        fieldProps: {
          switchLabel: COPY.calendarVisibility.label,
          description: COPY.calendarVisibility.subLabel,
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
      locationField,
      officeField,
      {
        type: 'select' as const,
        name: 'ext_interview_template_id',
        label: 'HackerRank Template',
        placeholder: 'Select template',
        options: hackerRankTemplateOptions,
        loading: hrLoading,
        // Required when the conditional fires (location=hackerrank).
        // FormBuilder's schemaGenerator runs the required check inside
        // the `superRefine` for conditional fields, so the validation
        // only triggers when the user actually picks HackerRank.
        required: 'HackerRank template is required',
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'hackerrank',
        },
      },
      {
        type: 'select' as const,
        name: 'host',
        label: COPY.host.label,
        tooltip: COPY.host.tooltip,
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'zoom',
        },
        options: [
          {
            label: COPY.host.options.organizer,
            value: 'organizer',
          },
          {
            label: COPY.host.options.interviewer,
            value: 'one_of_the_interviewers',
          },
        ],
      },
      noteTakerField,
    ],
    [
      interviewerField,
      interviewerPoolFields,
      locationField,
      officeField,
      hackerRankTemplateOptions,
      hrLoading,
      noteTakerField,
      shouldShowCalendarRow,
      calendarOptions,
      isCalendarsLoading,
      calendars,
    ]
  );

  const defaultValues = useMemo(
    () => ({
      interviewName: stageName ? `Interview - ${stageName}` : '',
      interview_type: 'one_on_one',
      interviewTypeRadio: 'manual_selection',
      participant_ids: [],
      interview_pool_id: '',
      duration: 30,
      schedule_selection: 'manual',
      calendar_visibility: false,
      office: '',
      host: 'organizer',
      location: defaultLocation,
      enable_note_taker: noteTakerMandatory,
      // `undefined` (not `null`) — FormBuilder's Zod union for the
      // conditional `required` rejects `null`.
      ext_interview_template_id: undefined,
      // Prefill the admin's default calendar (`is_default`) when one is
      // configured in Scheduling preferences; otherwise fall back to the
      // "Organizer's calendar" sentinel, which leaves `calendar_info`
      // unset on the wire so the schedule wizard picks the recruiter's
      // primary at schedule time. The user can still pick a different
      // shared calendar from the dropdown (via the field's `onChange`).
      calendar_info_picker:
        defaultCalendar?.provider_calendar_id ?? ORGANIZER_CALENDAR_VALUE,
      calendar_info: defaultCalendar?.provider_calendar_id
        ? {
            provider_calendar_id: defaultCalendar.provider_calendar_id,
            is_primary: Boolean(defaultCalendar.is_primary),
          }
        : undefined,
    }),
    [stageName, defaultLocation, noteTakerMandatory, defaultCalendar]
  );

  return { schema, defaultValues, defaultLocation };
};
